import React, { useEffect, useState } from 'react';
import {
  TaskyEmptyState,
  TaskyErrorState,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
} from '../../tasky';
import adminBehaviorService, { BehaviorRange, PageStat } from '../../../services/api/adminBehaviorService';
import { formatMs, formatNumber, formatPercent } from './format';

interface Props {
  range: BehaviorRange;
}

/**
 * page_pattern ごとの表示数・滞在時間・離脱率・エラー率。行を開くと上位クリックを表示。
 */
const PagesTab: React.FC<Props> = ({ range }) => {
  const [pages, setPages] = useState<PageStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminBehaviorService
      .getPages(range)
      .then((result) => {
        if (!cancelled) setPages(result);
      })
      .catch(() => {
        if (!cancelled) setError('ページ一覧の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <TaskySpinner />
      </div>
    );
  }
  if (error) return <TaskyErrorState title={error} />;
  if (pages.length === 0) return <TaskyEmptyState title="この期間のページビューはありません" />;

  return (
    <div className="overflow-x-auto">
      <TaskyTable className="min-w-[900px]">
        <TaskyTableHead>
          <TaskyTableRow>
            <TaskyTableHeaderCell>ページ</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>PV</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>ユーザー</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>セッション</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>滞在 平均</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>中央値</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>p90</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>離脱率</TaskyTableHeaderCell>
            <TaskyTableHeaderCell>APIエラー率</TaskyTableHeaderCell>
          </TaskyTableRow>
        </TaskyTableHead>
        <TaskyTableBody>
          {pages.map((page) => (
            <React.Fragment key={page.page_pattern}>
              <TaskyTableRow
                className="cursor-pointer"
                onClick={() => setExpanded(expanded === page.page_pattern ? null : page.page_pattern)}
                data-testid={`page-row-${page.page_pattern}`}
              >
                <TaskyTableCell className="font-mono text-xs">{page.page_pattern}</TaskyTableCell>
                <TaskyTableCell>{formatNumber(page.views)}</TaskyTableCell>
                <TaskyTableCell>{formatNumber(page.unique_users)}</TaskyTableCell>
                <TaskyTableCell>{formatNumber(page.unique_sessions)}</TaskyTableCell>
                <TaskyTableCell>{formatMs(page.avg_duration_ms)}</TaskyTableCell>
                <TaskyTableCell>{formatMs(page.median_duration_ms)}</TaskyTableCell>
                <TaskyTableCell>{formatMs(page.p90_duration_ms)}</TaskyTableCell>
                <TaskyTableCell className={page.exit_rate >= 50 ? 'text-destructive' : ''}>
                  {formatPercent(page.exit_rate)}
                </TaskyTableCell>
                <TaskyTableCell className={page.api_error_rate > 0 ? 'text-destructive' : ''}>
                  {formatPercent(page.api_error_rate)}
                </TaskyTableCell>
              </TaskyTableRow>
              {expanded === page.page_pattern && (
                <TaskyTableRow>
                  <TaskyTableCell colSpan={9} className="bg-warm-bg">
                    <div className="text-xs">
                      <span className="font-semibold text-dark">上位クリック: </span>
                      {page.top_clicks.length === 0 ? (
                        <span className="text-warm-text-muted">なし</span>
                      ) : (
                        page.top_clicks.map((click) => (
                          <span key={click.event_name} className="mr-3 inline-block">
                            {click.event_name}
                            <span className="ml-1 text-warm-text-muted">({formatNumber(click.count)})</span>
                          </span>
                        ))
                      )}
                    </div>
                  </TaskyTableCell>
                </TaskyTableRow>
              )}
            </React.Fragment>
          ))}
        </TaskyTableBody>
      </TaskyTable>
    </div>
  );
};

export default PagesTab;
